import React, { useEffect, useMemo, useState } from 'react';
import { BarList, Btn, DataTable, Loading, Msg, Section, Select, useAdminGet } from '../kit';
import { RowClick } from '../search/StepTimings';
import {
  MODE_LABEL, type RecrawlCtx, RetryMsg, RUN_STATUS, S, StatusPill, arr, fmtAgo, fmtDur, fmtIST, fmtMsDur, fmtN, fmtUsd, isLive, n0,
  num, obj, str, useLiveGet, usePoll, useStatusMix,
} from './shared';
import { RunAllModal, RunStartModal } from './RunStart';

/**
 * Recrawl → Runs: the run history (recrawl.runs through GET runs {code?,
 * status?, limit}), filterable by university and status, with the status mix
 * as a ranked list. A row opens the run drawer (RunDrawer.tsx). The list
 * refreshes every 10 s while any run on it is still going.
 *
 * Below it, run insights for the last 12 months (GET insights): estimated
 * enrichment spend per month, run durations per university and the slowest
 * steps — each a ranked list with the numbers beside the bars.
 *
 * RunTable is also the run list of the Overview and of the university drawer.
 * "Start a run…" and "Queue all universities…" open the run dialogs (RunStart.tsx).
 */

/** A RunView flattened for DataTable (sortable numbers, one column per count). */
export const runRow = (r: any) => {
  const v = obj(r);
  const c = obj(v.counts);
  const g = obj(v.guard);
  const reasons = [...arr(g.reasons), ...arr(obj(g.approval).reasons)].map(str).filter(Boolean);
  return {
    id: num(v.id), code: str(v.code), name: str(v.name), status: str(v.status), mode: str(v.mode),
    trigger: str(v.trigger), current_step: str(v.current_step), created_at: v.created_at || null,
    started_at: v.started_at || null, finished_at: v.finished_at || null, duration: num(v.duration_s),
    mapped: num(c.mapped), new: num(c.new), modified: num(c.modified), moved: num(c.moved), removed_proposed: num(c.removed_proposed),
    readded: num(c.readded), missing: num(c.missing), errors: num(c.errors),
    est_usd: num(obj(v.cost).est_usd), requested_by: str(v.requested_by), approved_by: str(v.approved_by),
    guard: Array.from(new Set(reasons)).join('; '), error: str(v.error), queue_position: num(v.queue_position),
    _run: v,
  };
};

const LABELS: Record<string, string> = {
  id: 'Run', code: 'University', removed_proposed: 'Removals', est_usd: 'Est. cost', created_at: 'Created',
  started_at: 'Started', finished_at: 'Finished', current_step: 'Step', readded: 'Re-added', guard: 'Guard / approval',
  moved: 'Moved',
};

export const RunTable: React.FC<{
  runs: any[]; ctx: RecrawlCtx; showUni?: boolean; csvName?: string; maxHeight?: string; empty?: string; tableId?: string;
}> = ({ runs, ctx, showUni = true, csvName, maxHeight, empty, tableId }) => {
  const rows = useMemo(() => runs.map(runRow).filter(r => r.id !== null), [runs]);
  const cols = ['id', ...(showUni ? ['code'] : []), 'status', 'mode', 'trigger', 'created_at', 'duration',
    'new', 'modified', 'removed_proposed', 'est_usd'];
  return (
    <RowClick onRow={k => ctx.openRun(k)}>
      <DataTable rows={rows} columns={cols} labels={LABELS} csvName={csvName} maxHeight={maxHeight}
        tableId={tableId || `recrawl_runs_${showUni ? 'all' : 'uni'}`}
        optionalColumns={['current_step', 'started_at', 'finished_at', 'mapped', 'moved', 'readded', 'missing', 'errors',
          'requested_by', 'approved_by', 'guard', 'error']}
        empty={empty || 'No runs yet.'}
        render={{
          id: r => <span data-row-key={r.id} className="font-medium text-primary-700">#{r.id}</span>,
          code: r => (
            <span title={r.name}>
              <b>{r.code}</b> <span className="text-grayscale-1100 text-xs">{r.name && r.name !== r.code ? r.name : ''}</span>
            </span>
          ),
          status: r => (
            <span className="inline-flex items-center gap-1">
              <StatusPill status={r.status} />
              {r.queue_position && r.status === 'queued' ? <span className="text-[10px] text-grayscale-1100">{r.queue_position === 1 ? 'next in the queue' : `#${r.queue_position} in the queue`}</span> : null}
              {r.status === 'running' && r.current_step ? <span className="text-[10px] text-grayscale-1100">{r.current_step}</span> : null}
            </span>
          ),
          mode: r => <span>{MODE_LABEL[r.mode] || r.mode || '—'}</span>,
          created_at: r => <span title={fmtIST(r.created_at)}>{fmtAgo(r.created_at) || '—'}</span>,
          started_at: r => <span>{fmtIST(r.started_at)}</span>,
          finished_at: r => <span>{fmtIST(r.finished_at)}</span>,
          duration: r => <span>{fmtDur(r.duration)}</span>,
          est_usd: r => <span>{r.est_usd === null ? '—' : fmtUsd(r.est_usd)}</span>,
          guard: r => <span title={r.guard} className="text-xs">{r.guard || '—'}</span>,
          error: r => <span title={r.error} className="text-xs text-red-700">{r.error || '—'}</span>,
        }} />
    </RowClick>
  );
};

const STATUS_FILTERS: [string, string][] = [
  ['', 'All statuses'],
  ['queued,running,promoting,post_load', 'Going now (queued / running / promoting / post-load)'],
  ['awaiting_approval,held', 'Needs a person (awaiting approval / held)'],
  ['awaiting_approval', 'Awaiting approval'], ['held', 'Held'], ['done', 'Done'], ['failed', 'Failed'],
  ['interrupted', 'Interrupted'], ['cancelled', 'Cancelled / rejected'],
];

/** One ranked row: label, a bar against the largest value, the numbers. */
const Rank: React.FC<{ label: React.ReactNode; value: number; max: number; right: React.ReactNode; title?: string; onClick?: () => void }> =
  ({ label, value, max, right, title, onClick }) => (
  <div title={title} onClick={onClick}
    className={`flex flex-col gap-0.5 text-sm rounded px-1 py-0.5 ${onClick ? 'cursor-pointer hover:bg-primary-100/40' : ''}`}>
    <div className="flex items-baseline justify-between gap-2 min-w-0">
      <span className="truncate text-grayscale-1200">{label}</span>
      <span className="text-xs text-grayscale-1200 whitespace-nowrap">{right}</span>
    </div>
    <div className="bg-grayscale-200 rounded-full h-2 overflow-hidden">
      <div className="h-2 bg-primary-500 rounded-full" style={{ width: `${max > 0 ? Math.max(1, (value / max) * 100) : 0}%` }} />
    </div>
  </div>
);

const RunInsights: React.FC<{ ctx: RecrawlCtx }> = ({ ctx }) => {
  const res = useAdminGet<any>(S, 'insights', {}, [ctx.tick]);
  const d = obj(res.data);
  const spend = obj(d.spend);
  const dur = obj(d.durations);
  const months = arr(spend.by_month);
  const unis = arr(dur.by_university);
  const steps = arr(dur.by_step);
  const maxUsd = Math.max(0, ...months.map(m => n0(m.usd)));
  const maxDur = Math.max(0, ...unis.map(u => n0(u.median_s)));
  const maxStep = Math.max(0, ...steps.map(x => n0(x.avg_ms)));
  if (res.loading && !res.data) return <Loading label="Loading run insights…" />;
  if (res.error && !res.data) return <Msg kind="error">{res.error}</Msg>;
  return (
    <div className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3 items-start">
      <Section title={`Enrichment spend — ${fmtUsd(spend.total_usd)} in 12 months`}>
        {!months.length ? <div className="text-sm text-grayscale-1100">No enrichment yet.</div> : months.slice().reverse().map(m => (
          <Rank key={str(m.month)} label={str(m.month)} value={n0(m.usd)} max={maxUsd}
            right={<><b>{fmtUsd(m.usd)}</b> · {fmtN(m.records)} records · {fmtN(m.runs)} runs</>} />
        ))}
        {num(spend.total_records) !== null && <div className="text-[11px] text-grayscale-1100">Estimated from records × the per-record estimate; {fmtN(spend.total_records)} records in all.</div>}
      </Section>
      <Section title="Run duration per university (median)">
        {!unis.length ? <div className="text-sm text-grayscale-1100">No finished run yet.</div> : unis.map(u => (
          <Rank key={str(u.code)} label={<b>{str(u.code)}</b>} value={n0(u.median_s)} max={maxDur}
            onClick={() => ctx.openUniversity(str(u.code))} title="Open the university"
            right={<><b>{fmtDur(u.median_s)}</b> · max {fmtDur(u.max_s)} · {fmtN(u.runs)} run{n0(u.runs) === 1 ? '' : 's'}</>} />
        ))}
      </Section>
      <Section title="Slowest steps (average)">
        {!steps.length ? <div className="text-sm text-grayscale-1100">No step timings yet.</div> : steps.slice(0, 15).map(x => (
          <Rank key={str(x.step_id)} label={<code className="text-xs">{str(x.step_id)}</code>} value={n0(x.avg_ms)} max={maxStep}
            right={<><b>{fmtMsDur(x.avg_ms)}</b> · p50 {fmtMsDur(x.p50_ms)} · max {fmtMsDur(x.max_ms)} · {fmtN(x.n)}×</>} />
        ))}
      </Section>
    </div>
  );
};

const Runs: React.FC<{ ctx: RecrawlCtx; focus: ({ code?: string; status?: string; n: number }) | null }> = ({ ctx, focus }) => {
  const [code, setCode] = useState('');
  const [status, setStatus] = useState('');
  const [limit, setLimit] = useState('100');
  const [starting, setStarting] = useState<'' | 'one' | 'all'>('');
  useEffect(() => {
    if (!focus) return;
    setCode(str(focus.code).toUpperCase());
    setStatus(str(focus.status));
  }, [focus?.n]);  // eslint-disable-line react-hooks/exhaustive-deps

  const params: Record<string, string | number> = { limit: Number(limit) || 100 };
  if (code) params.code = code;
  if (status) params.status = status;
  // useLiveGet: a failed poll keeps the list (and so `live` and the polling) instead of blanking it.
  const res = useLiveGet<any>('runs', params, [code, status, limit, ctx.tick]);
  const runs = useMemo(() => arr(res.data?.runs), [res.data]);
  const live = runs.some(isLive);
  usePoll(res.reload, 10000, live);
  const mix = useStatusMix(runs);
  const total = num(res.data?.total);

  const statusOpts = STATUS_FILTERS.map(([k]) => k);
  const statusLabels = Object.fromEntries(STATUS_FILTERS) as Record<string, string>;
  if (status && !statusOpts.includes(status)) { statusOpts.push(status); statusLabels[status] = status; }
  const codeOpts = ['', ...ctx.universities.map(u => u.code)];
  if (code && !codeOpts.includes(code)) codeOpts.push(code);
  const codeLabels: Record<string, string> = { '': 'All universities' };
  ctx.universities.forEach(u => { codeLabels[u.code] = `${u.code} — ${u.name}`; });

  return (
    <>
      <Section title="Run history"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={code} onChange={setCode} options={codeOpts} labels={codeLabels} />
            <Select value={status} onChange={setStatus} options={statusOpts} labels={statusLabels} />
            <Select value={limit} onChange={setLimit} options={['50', '100', '200', '500']}
              labels={{ 50: 'Last 50', 100: 'Last 100', 200: 'Last 200', 500: 'Last 500' }} />
            <Btn small kind="primary" onClick={() => setStarting('one')}>Start a run…</Btn>
            <Btn small onClick={() => setStarting('all')}>Queue all universities…</Btn>
          </div>
        }>
        {starting === 'one' && <RunStartModal ctx={ctx} code={code || undefined} onClose={() => setStarting('')} />}
        {starting === 'all' && <RunAllModal ctx={ctx} onClose={() => setStarting('')} />}
        <div className="text-xs text-grayscale-1100">
          {total !== null ? <>Showing <b className="text-grayscale-1300">{runs.length.toLocaleString()}</b> of {total.toLocaleString()} matching runs.</> : null}
          {live && <span> Refreshing every 10 s while a run on this list is going.</span>}
          {' '}Click a run for its steps, logs, staged changes and the approve / reject controls.
        </div>
        {res.error && <RetryMsg error={res.error} onRetry={res.reload} stale={res.stale} polling={live} />}
        {res.loading && !res.data ? <Loading /> : (
          <RunTable runs={runs} ctx={ctx} csvName="recrawl_runs" maxHeight="34rem"
            empty={code || status ? 'No runs match these filters.' : 'No runs yet — start one with Start a run.'} />
        )}
      </Section>
      {mix.length > 0 && (
        <Section title="Status mix of these runs">
          <BarList items={mix.map(m => ({ label: RUN_STATUS[m.status]?.label || m.status, value: m.n }))}
            onSelect={label => {
              const hit = mix.find(m => (RUN_STATUS[m.status]?.label || m.status) === label);
              if (hit) setStatus(s => (s === hit.status ? '' : hit.status));
            }}
            activeLabel={status && !status.includes(',') ? (RUN_STATUS[status]?.label || status) : undefined} />
        </Section>
      )}
      <RunInsights ctx={ctx} />
    </>
  );
};

export default Runs;
